import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getSubscriptionFlowApiClient } from "@shared/apis/sidekick/api-factories";
import { GET_SUBSCRIPTION_FLOW_QUERY_KEY } from "./useGetSubscriptionFlow";
import { SendSubscriptionTransactionInfoResponse } from "@shared/apis/sidekick/generated";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { toast } from "@shared/services/toast";
import { appEventEmitter } from "@shared/events/app-event-emitter";

type Props = {
  onSuccess?: (data: SendSubscriptionTransactionInfoResponse) => void;
};

type Params = {
  subscriptionId: string;
  user: {
    name?: string;
    phoneNumber: string | null;
    mlsId?: string;
    officeName?: string;
  };
};

export const useSaveSubscriptionFlowTransactionInfo = (props?: Props) => {
  const { onSuccess } = props || {};
  const api = getSubscriptionFlowApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (params: Params) => {
      // TODO: Adjust to accept undefined
      const response =
        await api.subscriptionFlowControllerBeginSubscriptionSetup({
          id: params.subscriptionId,
          sendSubscriptionTransactionInfoRequestOmitted: {
            userName: params.user.name ?? "",
            acceptTermsOfService: true,
            userPhoneNumber: params.user.phoneNumber,
            userMlsId: params.user.mlsId ?? "",
            officeName: params.user.officeName ?? "",
          },
        });
      queryClient.invalidateQueries({
        queryKey: [GET_SUBSCRIPTION_FLOW_QUERY_KEY],
      });
      onSuccess?.(response);
      return response;
    },
    onError: async (error) => {
      console.error(`Failed to save subscription flow transaction info`, error);
      appEventEmitter.emit({
        eventType: "sidekick-registration-complete-failure",
      });
      const apiError = await apiResponseErrorHandler(error);
      if (apiError.status >= 400 && apiError.status < 500) {
        toast.error(apiError.errors[0], {
          toastId: "save-subscription-flow-user-data-error",
        });
        return;
      }
      toast.error(
        "Failed to save your details. Please try again later and if the error persists, please contact the support.",
        {
          toastId: "unexpected-save-subscription-flow-user-data-error",
        }
      );
    },
  });

  return mutation;
};
